/**
 * THE map from a job status to its colour. Nothing else decides one.
 *
 * Tom, 1 Nov 2026 (jobs page round 4): **orange is only ever the action
 * colour.** A status is never orange, so the one orange thing on a screen is
 * always the thing to press. Before this, On site was orange on the job page,
 * the Jobs list and the diary, and sat beside an orange Mark done that it
 * looked exactly like.
 *
 * The colour follows the status's `system_key`, never its NAME: accounts
 * rename their statuses, and `to_invoice`, `invoice_sent` and `completed`
 * all share `completed` (CLAUDE.md #14).
 *
 * | system key | means          | colour  |
 * |------------|----------------|---------|
 * | pending    | not booked     | neutral |
 * | scheduled  | booked         | blue    |
 * | active     | on site        | amber   |
 * | completed  | finished       | green   |
 * | cancelled  | cancelled      | red     |
 * | none       | the account's own (On hold) | charcoal |
 *
 * `travelling` shares `system_key: 'scheduled'` on purpose (an `active` key
 * would stamp `actual_start` when the van leaves), so it is told apart by
 * name, exactly as `board.ts` files it under Out now: an engineer on the way
 * is amber, with On site.
 *
 * The colour values themselves live in ONE block of `globals.css`
 * ("Status colour — the one map"): `.sc--<colour>` sets `--sc` (full colour,
 * for a small mark: a dot, a bar, a ring), `--sc-tint` (for an area) and
 * `--sc-ink` (for coloured TEXT, a darker shade that passes WCAG AA 4.5:1 on
 * its tint and on white). A component takes the class from
 * `statusColourClass` and reads the three properties; it never names a
 * colour of its own.
 *
 * Never colour alone: a status colour always travels with the status's word
 * or its icon.
 *
 * Directive-free on purpose: the server-rendered list and the client pages
 * both import it (CLAUDE.md #18).
 *
 * Guard: `src/lib/ui/__tests__/a-job-status-colour-comes-from-the-map.test.ts`.
 */
import { resolveStatusKey, TRAVELLING_STATUS_NAME, type StatusKeyMap } from '@/lib/jobs/status';

export type StatusColour = 'neutral' | 'blue' | 'amber' | 'green' | 'red' | 'charcoal';

export const STATUS_COLOURS: readonly StatusColour[] = ['neutral', 'blue', 'amber', 'green', 'red', 'charcoal'];

/** The table above, as data. */
export const STATUS_COLOUR_BY_KEY: Record<'pending' | 'scheduled' | 'active' | 'completed' | 'cancelled', StatusColour> = {
  pending: 'neutral',
  scheduled: 'blue',
  active: 'amber',
  completed: 'green',
  cancelled: 'red',
};

/** A status with no system key is the account's own: charcoal. */
export const OWN_STATUS_COLOUR: StatusColour = 'charcoal';

/** The colour for a system key. Anything unrecognised is the account's own. */
export function statusColourForKey(key: string | null | undefined): StatusColour {
  if (!key) return OWN_STATUS_COLOUR;
  return (STATUS_COLOUR_BY_KEY as Record<string, StatusColour>)[key] ?? OWN_STATUS_COLOUR;
}

/**
 * The colour for a job's status NAME, through the account's key map (or its
 * `job_statuses` rows). `travelling` is amber, with On site.
 */
export function jobStatusColour(
  status: string | null | undefined,
  statuses: StatusKeyMap | readonly { name: string; system_key: string | null }[],
): StatusColour {
  if (!status) return 'neutral';
  if (status === TRAVELLING_STATUS_NAME) return 'amber';
  const keyMap: StatusKeyMap = Array.isArray(statuses)
    ? Object.fromEntries((statuses as readonly { name: string; system_key: string | null }[]).map(s => [s.name, s.system_key ?? null]))
    : (statuses as StatusKeyMap);
  return statusColourForKey(resolveStatusKey(status, keyMap));
}

/**
 * The class that carries a colour's three properties (`--sc`, `--sc-tint`,
 * `--sc-ink`). Pair it with a component class that reads them.
 */
export function statusColourClass(colour: StatusColour): string {
  return `sc--${colour}`;
}

/** The class for a job's status, in one call. */
export function jobStatusColourClass(
  status: string | null | undefined,
  statuses: StatusKeyMap | readonly { name: string; system_key: string | null }[],
): string {
  return statusColourClass(jobStatusColour(status, statuses));
}

/**
 * An ENGINEER's whereabouts that means a job status (Tom, 1 Nov 2026): an
 * engineer shown "On site" on the team pages, the Engineers sheet, the live
 * map and the chat's team map is on site at a JOB, so he wears the job's On
 * site colour, amber, read from the table above. The other whereabouts
 * (on the way to the next job, free, delayed, overrunning) are about the
 * person's day, not a job's status, and keep their own palette.
 */
export const ENGINEER_STATUS_COLOUR: Readonly<Record<string, StatusColour>> = {
  'on-site': STATUS_COLOUR_BY_KEY.active,
  // The chat's team map: a job on someone's day that has not started yet is
  // not-booked's neutral grey (Tom, 1 Nov 2026), so amber there only ever
  // means somebody is on it.
  'not-started': STATUS_COLOUR_BY_KEY.pending,
};

/** The `sc--*` class for an engineer's whereabouts, or '' when it is not a job status. */
export function engineerStatusColourClass(status: string | null | undefined): string {
  const colour = status ? ENGINEER_STATUS_COLOUR[status] : undefined;
  return colour ? statusColourClass(colour) : '';
}

/**
 * The full colours as hex, for the two places that cannot read a CSS custom
 * property: a Mapbox marker styled from script and the Static Images URL the
 * chat's team map builds. They are the `--sc` values of the "Status colour —
 * the one map" block in globals.css, and
 * `an-engineer-on-site-wears-the-job-colour.test.ts` holds the two equal.
 * `on` is the text that reads on the full colour (`--sc-on`), AA.
 */
export const STATUS_COLOUR_FILL_HEX: Readonly<Record<StatusColour, { fill: string; on: string }>> = {
  neutral: { fill: '#8a8785', on: '#231f20' },
  blue: { fill: '#1a62b5', on: '#ffffff' },
  amber: { fill: '#c47f00', on: '#231f20' },
  green: { fill: '#0f9e56', on: '#231f20' },
  red: { fill: '#c42b2b', on: '#ffffff' },
  charcoal: { fill: '#231f20', on: '#ffffff' },
};
